import { styled } from "goober";
import { nanoid } from "nanoid";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { useIsMobile } from "../hooks/useIsMobile";
import { connectorDirectory } from "../lib/connectorDirectory";
import { playClick } from "../lib/sounds";
import { ClickButton } from "./ClickButton";
import { ConnectorAvatarContextMenu } from "./ConnectorAvatarContextMenu";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { FacePile } from "./FacePile";
import { Link } from "./Link";
import { Tooltip } from "./Tooltip";
import { useMakeUrl, navigate, routes } from "../lib/urls";

const FlatLogoButton = styled(ClickButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 4px;
  transition: transform var(--motion-fast) var(--ease-out);

  &:hover {
    transform: scale(1.15);
  }
`;

const FLAT_GAP = 4;
// Width reserved for the trailing "+N" badge when the logos overflow.
const FLAT_OVERFLOW_RESERVE = 24;

const FlatRow = styled("div")`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${FLAT_GAP}px;
  min-width: 0;
`;

export type ConnectedAvatar = {
  id: string;
  name: string;
  app: ConnectorWithOAuth;
  // Adds a remove item to the context menu, when the pile has one.
  onRemove?: () => void;
  removeDisabled?: boolean;
};

export type DisconnectedAvatar = {
  id: string;
  appType: string;
  // The reference name in the workflow's config — the only identity that
  // survives when the connector itself is gone.
  slug?: string;
  // Wraps the faded opener with a caller affordance (e.g. a reconnect dropdown);
  // read-only callers leave it inert.
  wrap?: (opener: ReactNode) => ReactNode;
};

type Props = {
  disabled?: boolean;
  connected: readonly ConnectedAvatar[];
  disconnected?: readonly DisconnectedAvatar[];
  // When set, right-clicking a connected avatar opens a manage/remove context
  // menu; parentNoun labels removal (e.g. "step" or "chat"), margin is the gap in
  // px between the avatar and the menu.
  menu?: { parentNoun: string; margin: number };
  vertical?: boolean;
  itemSize?: 24 | 32;
  // Flat read-only strip (bare logos in a row) instead of the overlapping pile.
  flat?: boolean;
  // Collapse logos that overflow the available width into a trailing "+N" badge,
  // for width-constrained rows and columns (grid steps, list tables).
  collapseToFit?: boolean;
  // Flat only: horizontal alignment within the row. Defaults to left.
  align?: "start" | "end";
  // Flat only: expand across the available width. Defaults to true.
  fillAvailableWidth?: boolean;
  // Pile only: wraps the "+N" badge, e.g. with the add dropdown.
  wrapOverflow?: (badge: ReactNode) => ReactNode;
};

// The avatar pile shared by `ConnectorsBar` (which layers an add dropdown on
// top) and read-only views like grid steps. Resolved connectors open their app
// modal; referenced-but-missing types show faded.
export function ConnectorAvatars({
  connected,
  disabled,
  disconnected = [],
  menu,
  vertical = false,
  itemSize = 24,
  flat = false,
  collapseToFit = false,
  align = "start",
  fillAvailableWidth = true,
  wrapOverflow,
}: Props) {
  const makeUrl = useMakeUrl();
  const isMobile = useIsMobile();
  const menuSide = vertical ? (isMobile ? "right" : "left") : undefined;
  const size = itemSize === 32 && !flat ? 16 : 12;
  const outlineWidth = 2;
  const background = true;
  const [menuAnchorName] = useState(() => `--connector-menu-${nanoid()}`);
  const [appMenu, setAppMenu] = useState<{
    id: string;
    editTo: string;
  } | null>(null);

  const menuApp = connected.find((app) => app.id === appMenu?.id);
  const contextMenu = menu && appMenu && (
    <ConnectorAvatarContextMenu
      disabled={disabled}
      anchorName={menuAnchorName}
      side={menuSide ?? "bottom"}
      margin={menu.margin}
      editTo={appMenu.editTo}
      parentNoun={menu.parentNoun}
      onRemove={menuApp?.onRemove}
      removeDisabled={menuApp?.removeDisabled}
      onClose={() => setAppMenu(null)}
    />
  );

  const contextMenuHandler = (id: string) =>
    menu
      ? (e: React.MouseEvent) => {
          e.preventDefault();
          playClick();
          setAppMenu({
            id,
            editTo: makeUrl(routes.modal.connector, { id: id }),
          });
        }
      : undefined;

  const disconnectedMeta = (appType: string) => {
    const appDef = connectorDirectory.find(
      (a) => a.identifier.toLowerCase() === appType.toLowerCase()
    );
    return {
      name: appDef?.name ?? appType,
      identifier: appDef?.identifier ?? appType,
    };
  };

  if (flat) {
    if (connected.length === 0 && disconnected.length === 0) return null;
    const flatItems = [
      ...connected.map((app) => ({
        key: app.id,
        label: app.name,
        node: (
          <Tooltip content={app.name}>
            <FlatLogoButton
              data-card-chip
              style={
                {
                  anchorName:
                    appMenu?.id === app.id ? menuAnchorName : undefined,
                } as React.CSSProperties
              }
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(routes.modal.connector, { id: app.id });
              }}
              onContextMenu={contextMenuHandler(app.id)}
            >
              <ConnectorLogo app={app.app} size={size} />
            </FlatLogoButton>
          </Tooltip>
        ),
      })),
      ...disconnected.map((slot) => {
        const { name, identifier } = disconnectedMeta(slot.appType);
        const label = slot.slug ?? name;
        const opener = (
          <Tooltip content={`Connect ${label}`}>
            <span style={{ display: "flex" }} data-card-chip>
              <ConnectorLogo
                template={{ name: label, identifier }}
                size={size}
                faded
              />
            </span>
          </Tooltip>
        );
        return {
          key: slot.id,
          label,
          node: slot.wrap ? slot.wrap(opener) : opener,
        };
      }),
    ];
    return (
      <>
        {collapseToFit ? (
          <FlatAvatarRow
            items={flatItems}
            itemSize={size}
            align={align}
            fillAvailableWidth={fillAvailableWidth}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: align === "end" ? "flex-end" : "flex-start",
              gap: FLAT_GAP,
            }}
          >
            {flatItems.map((item) => (
              <Fragment key={item.key}>{item.node}</Fragment>
            ))}
          </div>
        )}
        {contextMenu}
      </>
    );
  }

  const connectedItems = connected.map((app) => {
    const manageTo = makeUrl(routes.modal.connector, { id: app.id });
    return {
      id: app.id,
      label: app.name,
      bare: true,
      node: (
        <Tooltip content={app.name} side={menuSide}>
          <div
            style={
              {
                cursor: "pointer",
                anchorName: appMenu?.id === app.id ? menuAnchorName : undefined,
              } as React.CSSProperties
            }
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={contextMenuHandler(app.id)}
          >
            <ConnectorLogoContainer
              size={itemSize}
              borderRadius={itemSize / 2}
              outline={false}
              outlineWidth={outlineWidth}
              background={background}
              hoverBackground={background}
            >
              <Link to={manageTo} style={{ display: "flex" }}>
                <ConnectorLogo app={app.app} size={size} />
              </Link>
            </ConnectorLogoContainer>
          </div>
        </Tooltip>
      ),
    };
  });

  const disconnectedItems = disconnected.map((slot) => {
    const { name, identifier } = disconnectedMeta(slot.appType);
    const label = slot.slug ?? name;
    const opener = (
      <Tooltip content={`Connect ${label}`} side={menuSide}>
        <ConnectorLogoContainer
          size={itemSize}
          borderRadius={itemSize / 2}
          outline={false}
          outlineWidth={outlineWidth}
          background={background}
          hoverBackground={background}
        >
          <ConnectorLogo
            template={{ name: label, identifier }}
            size={size}
            faded
          />
        </ConnectorLogoContainer>
      </Tooltip>
    );
    return {
      id: slot.id,
      label: `Connect ${label}`,
      bare: true,
      node: slot.wrap ? slot.wrap(opener) : opener,
    };
  });

  const items = [...connectedItems, ...disconnectedItems];
  if (items.length === 0) return null;

  return (
    <>
      <FacePile
        itemSize={itemSize}
        outlineWidth={outlineWidth}
        items={items}
        cutout
        collapseToFit={collapseToFit}
        vertical={vertical}
        reverse={vertical}
        wrapOverflow={wrapOverflow}
      />
      {contextMenu}
    </>
  );
}

// Width-aware flat logo strip: renders as many fixed-size logos as fit the
// available width and rolls the rest into a trailing "+N".
function FlatAvatarRow({
  items,
  itemSize,
  align,
  fillAvailableWidth,
}: {
  items: { key: string; label: string; node: ReactNode }[];
  itemSize: number;
  align: "start" | "end";
  fillAvailableWidth: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const computeVisibleCount = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const width = root.clientWidth;
    if (width === 0) return;

    const slot = itemSize + FLAT_GAP;
    const fitAll = Math.floor((width + FLAT_GAP) / slot);
    const next =
      fitAll >= items.length
        ? items.length
        : Math.max(0, Math.floor((width - FLAT_OVERFLOW_RESERVE) / slot));

    setVisibleCount((current) => (current === next ? current : next));
  }, [items.length, itemSize]);

  useLayoutEffect(() => {
    computeVisibleCount();
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver(computeVisibleCount);
    observer.observe(root);
    return () => observer.disconnect();
  }, [computeVisibleCount]);

  const visible = items.slice(0, visibleCount);
  const hidden = items.slice(visibleCount);

  return (
    <FlatRow
      ref={rootRef}
      style={{
        flex: fillAvailableWidth ? 1 : "0 1 auto",
        justifyContent: align === "end" ? "flex-end" : "flex-start",
      }}
    >
      {visible.map((item) => (
        <Fragment key={item.key}>{item.node}</Fragment>
      ))}
      {hidden.length > 0 && (
        <Tooltip content={hidden.map((h) => h.label).join(", ")}>
          {/* Reuses GridStep's `.counter` class so the badge keeps its backdrop on card hover/selected. */}
          <div className="counter" data-card-chip>
            +{hidden.length}
          </div>
        </Tooltip>
      )}
    </FlatRow>
  );
}
